/**
 * agentReplay.js — re-play a finished (or running) case from its stored events (CONTRACT §10.2,
 * §10.4 AuditView "Replay case").
 *
 * The events of GET /v1/cases/{id}/events are fed through agentReducer into a SEPARATE replay
 * state, keeping their relative timing divided by `speed`. The live state is never touched, and
 * nothing is sent to the server or the stage: the replay is a read-only copy of the stream.
 *
 * Pure: timers are injected (`schedule(fn, ms) -> handle`, `cancel(handle)`, `now() -> ms`), so
 * the tests drive it with a fake clock.
 */

import { agentReducer, initialAgentState } from './agentReducer.js';

const tsOf = (e) => {
  const n = Date.parse(e?.ts ?? '');
  return Number.isFinite(n) ? n : null;
};

/**
 * @param {object[]} events   Envelopes, oldest first
 * @param {object} opts
 * @param {number} [opts.speed=4]            replay time = real time / speed (<= 0: all at once)
 * @param {(state, event) => void} [opts.onEvent]  after each event is applied
 * @param {(state) => void} [opts.onDone]
 * @param {() => number} [opts.now]
 * @param {(fn, ms) => any} [opts.schedule]
 * @param {(handle) => void} [opts.cancel]
 * @param {number} [opts.maxGapMs=Infinity]  cap on one wait (a case that waited 20 minutes for an
 *                                           approval need not stall the replay)
 * @param {object} [opts.initial]           starting state (default: empty)
 * @returns {{stop(): void, readonly state: object, readonly done: boolean, readonly index: number}}
 */
export function createReplay(events, {
  speed = 4, onEvent = null, onDone = null,
  now = () => Date.now(),
  schedule = (fn, ms) => setTimeout(fn, ms),
  cancel = (h) => clearTimeout(h),
  maxGapMs = Infinity,
  initial = initialAgentState,
} = {}) {
  const list = (Array.isArray(events) ? events : [])
    .filter((e) => e && typeof e.type === 'string')
    .map((e) => ({ ...e, replayed: true }));
  // The replay is its own client: it must not re-apply the server seq rule against live state,
  // so it starts at seq 0, and replayed envelopes keep their original seqs.
  let state = { ...initial, seq: 0, status: 'replay' };
  let index = 0;
  let handle = null;
  let stopped = false;
  let finished = false;

  // Target offsets (ms from start) per event, gaps scaled by speed and capped.
  const offsets = [];
  let acc = 0;
  for (let i = 0; i < list.length; i += 1) {
    if (i > 0) {
      const a = tsOf(list[i - 1]);
      const b = tsOf(list[i]);
      const gap = a !== null && b !== null && b > a && speed > 0 ? (b - a) / speed : 0;
      acc += Math.min(gap, maxGapMs);
    }
    offsets.push(acc);
  }

  const startedAt = now();

  const finish = () => {
    if (finished) return;
    finished = true;
    handle = null;
    if (typeof onDone === 'function') onDone(state);
  };

  const step = () => {
    handle = null;
    if (stopped) return;
    // Apply every event that is due now (several can share a timestamp).
    const elapsed = now() - startedAt;
    while (index < list.length && (speed <= 0 || offsets[index] <= elapsed)) {
      const event = list[index];
      state = agentReducer(state, event);
      index += 1;
      if (typeof onEvent === 'function') onEvent(state, event);
      if (stopped) return;
    }
    if (index >= list.length) { finish(); return; }
    const wait = Math.max(0, offsets[index] - (now() - startedAt));
    handle = schedule(step, wait);
  };

  if (list.length === 0) {
    handle = schedule(() => { if (!stopped) finish(); }, 0);
  } else {
    handle = schedule(step, 0);
  }

  return {
    stop() {
      if (stopped) return;
      stopped = true;
      if (handle !== null) cancel(handle);
      handle = null;
    },
    get state() { return state; },
    get done() { return finished; },
    get index() { return index; },
  };
}

/** The replay shown for `caseId`: only one started for that case, else null (the live view). */
export function replayFor(replay, caseId) {
  return replay && replay.caseId === caseId ? replay : null;
}

/**
 * Load a case's stored events for "Replay case". Resolves null when the active case changed while
 * they loaded (`currentCaseId()` is read on resolution), so a late replay never takes over, and
 * locks as read only, the case the user switched to.
 */
export async function loadReplayEvents(caseId, eventsFn, currentCaseId) {
  const events = await eventsFn(caseId);
  return currentCaseId() === caseId ? events : null;
}
